import { createSlice } from '@reduxjs/toolkit';
import { BnplCheckoutApi } from '../util/bnpl-checkout-api';
import getConstants from '../util/constants';
import { AppThunkDispatch } from '../util/store';
import { ApplicationStep, PlanResponse } from '@payright/types-bnpl-checkout-api';

const { CONTACT_US_PHONE_NUMBER } = getConstants();

type InitialState = {
  loading: boolean;
  activeRequests: Array<'getPlan' | 'consentToPlan' | 'acceptLoanPurpose'>;
  plan: PlanResponse | null;
  hasErrors: boolean;
  errorTitle: string | null;
  errorMessage: string | null;
  hasConsent: boolean;
};

export const initialState: InitialState = {
  loading: false,
  activeRequests: [],
  plan: null,
  hasErrors: false,
  errorTitle: null,
  errorMessage: null,
  hasConsent: false,
};

const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    fetchExistingPlanBegin: (state) => {
      return {
        ...state,
        loading: true,
        activeRequests: [...state.activeRequests, 'getPlan'],
      };
    },
    fetchExistingPlanSuccess: (state, action) => {
      return {
        ...state,
        loading: false,
        activeRequests: state.activeRequests.filter((request) => request !== 'getPlan'),
        plan: action.payload,
      };
    },
    fetchExistingPlanFailure: (state, action) => {
      return {
        ...state,
        loading: false,
        activeRequests: state.activeRequests.filter((request) => request !== 'getPlan'),
        hasErrors: true,
        errorMessage: action.payload.errorMessage,
      };
    },
    acceptLoanPurposeBegin: (state) => {
      return {
        ...state,
        loading: true,
        activeRequests: [...state.activeRequests, 'acceptLoanPurpose'],
      };
    },
    acceptLoanPurposeSuccess: (state, action) => {
      return {
        ...state,
        loading: false,
        activeRequests: state.activeRequests.filter((request) => request !== 'acceptLoanPurpose'),
        plan: state.plan
          ? {
              ...state.plan,
              applicationStep: action.payload.nextStep,
            }
          : null,
      };
    },
    acceptLoanPurposeFailure: (state, action) => {
      return {
        ...state,
        loading: false,
        hasErrors: true,
        errorTitle: action.payload.errorTitle,
        errorMessage: action.payload.errorMessage,
        activeRequests: state.activeRequests.filter((request) => request !== 'acceptLoanPurpose'),
      };
    },
    consentToPlanBegin: (state) => {
      return {
        ...state,
        loading: true,
        activeRequests: [...state.activeRequests, 'consentToPlan'],
      };
    },
    consentToPlanSuccess: (state, action) => {
      return {
        ...state,
        hasConsent: action.payload.hasConsent,
        loading: false,
        activeRequests: state.activeRequests.filter((request) => request !== 'consentToPlan'),
      };
    },
    consentToPlanFailure: (state, action) => {
      return {
        ...state,
        loading: false,
        activeRequests: state.activeRequests.filter((request) => request !== 'consentToPlan'),
        hasErrors: true,
        errorTitle: action.payload.errorTitle,
        errorMessage: action.payload.errorMessage,
      };
    },
  },
});

export const {
  fetchExistingPlanBegin,
  fetchExistingPlanSuccess,
  fetchExistingPlanFailure,
  acceptLoanPurposeBegin,
  acceptLoanPurposeSuccess,
  acceptLoanPurposeFailure,
  consentToPlanBegin,
  consentToPlanSuccess,
  consentToPlanFailure,
} = planSlice.actions;

export const fetchPlan = (checkoutId: string, planId: string) => {
  return async (dispatch: any) => {
    try {
      const bnplCheckoutApi = new BnplCheckoutApi();
      dispatch(fetchExistingPlanBegin());
      const response = await bnplCheckoutApi.getPlan(checkoutId, planId);
      dispatch(fetchExistingPlanSuccess(response));
    } catch (error) {
      dispatch(
        fetchExistingPlanFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
    }
  };
};

export const consentToPlan = (
  checkoutId: string
): ((dispatch: AppThunkDispatch) => Promise<ApplicationStep | undefined>) => {
  return async (dispatch: AppThunkDispatch) => {
    try {
      dispatch(consentToPlanBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();
      const nextStep = await bnplCheckoutApi.planConsent(checkoutId);
      dispatch(consentToPlanSuccess({ hasConsent: true }));
      return nextStep;
    } catch (error) {
      dispatch(
        consentToPlanFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
      return undefined;
    }
  };
};

export const acceptLoanPurpose = (planId: string) => {
  return async (dispatch: AppThunkDispatch) => {
    try {
      dispatch(acceptLoanPurposeBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();
      const result = await bnplCheckoutApi.acceptLoanPurpose(planId);
      dispatch(acceptLoanPurposeSuccess({ nextStep: result.nextStep }));
      return { success: true, nextStep: result.nextStep };
    } catch (error) {
      dispatch(
        acceptLoanPurposeFailure({
          errorTitle: 'There was an unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
      return { success: false };
    }
  };
};

export default planSlice.reducer;
